import { FieldMappingGroup, FieldType } from '@components/text-editors/addons/field-mapping';

/**
 * Narrow a variable-group list to the fields a typed target can accept.
 *
 * A field with no declared type matches any target, and groups left with no
 * fields are dropped so the mapping picker shows no vacant sections.
 *
 * The send sidebar gets this applied for it by `useVariableGroups`, which asks
 * for its types up front; the config panels receive one already-built generic
 * list as a prop and apply it themselves. Both routes end here, so the rule has
 * exactly one implementation.
 *
 * Lives in its own module rather than beside its first caller: the global
 * Configure panel (journey-config-sidebar's base Email row), the per-field
 * ValueInput and helpers.tsx's useVariableGroups all need it, and importing it
 * from a component module would drag the whole editor stack into callers that
 * only want the rule.
 *
 * `types` undefined means "no type restriction" — every field is kept, and
 * empty groups are still dropped. That is the shape useVariableGroups needs,
 * which asks for its types up front and may ask for none.
 */
export const filterVariableGroupsByTypes = (
  groups: FieldMappingGroup[],
  types: FieldType[] | undefined,
): FieldMappingGroup[] => {
  return groups
    .map((group) => ({
      ...group,
      fields: (group.fields ?? []).filter(
        (field) => !types || !field.type || types.includes(field.type),
      ),
    }))
    .filter((group) => group.fields.length > 0);
};

/**
 * Drop the fields whose type a target cannot meaningfully hold.
 *
 * The mirror of filterVariableGroupsByTypes, for a target that accepts most
 * things but not everything: a text field is handed the whole variable list,
 * which is how CV and Cover letter — both typed `file` — ended up offered
 * there. Mapping one in sends whatever string the file resolves to rather than
 * a file, so the picker should not suggest it in the first place.
 *
 * A field with no declared type is kept, matching the allowlist rule: an
 * untyped variable is assumed usable anywhere.
 */
export const excludeVariableGroupTypes = (
  groups: FieldMappingGroup[],
  types: FieldType[],
): FieldMappingGroup[] => {
  return groups
    .map((group) => ({
      ...group,
      fields: (group.fields ?? []).filter((field) => !field.type || !types.includes(field.type)),
    }))
    .filter((group) => group.fields.length > 0);
};

/** File-shaped variable types, which only a file target can hold. */
export const FILE_VARIABLE_TYPES: FieldType[] = ['file', 'file_multi'];
